'use client';

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Calculator,
  ArrowRight,
  Lightning,
  Ruler,
  House,
  ArrowSquareOut,
  Check,
  Globe,
  Envelope,
  CloudSun,
  Calendar,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { getAppLink } from '@/lib/links';
import { AnimatedSection } from '@/components/marketing/AnimatedSection';

const widgetBenefits = [
  'Address lookup with auto-complete',
  'ArcGIS building footprint (free)',
  'Optional Google Solar API for higher accuracy',
  'Ballpark estimate with material breakdown',
  'Lead capture with property data pre-loaded',
  'White-label embed for any contractor website',
];

const measurementBenefits = [
  'Address-only input — no manual drawing',
  'AI roof detection and facet analysis',
  'Linear measurements for ridges, hips, valleys, eaves',
  'Pitch detection and material quantities',
  '3D imagery with measurement overlays',
  'ESX export for Xactimate (planned)',
];

export function EstimatorPage() {
  const [address, setAddress] = useState('');
  const reduced = useReducedMotion();

  const handleDemoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.trim()) return;
    const encoded = encodeURIComponent(address.trim());
    window.location.assign(getAppLink(`/estimate?address=${encoded}`));
  };

  return (
    <>
      <SEOHead
        title="Free Instant Roof Estimate Calculator | Rooflyzer"
        description="Get satellite-based roof estimates in seconds. Try the live Instant Estimate Widget, or learn about the Professional Measurement Tool coming to Rooflyzer."
        canonicalUrl="/estimator"
        keywords="roof estimate calculator, instant roof estimate, satellite roof measurement, roof replacement estimate, roofing estimate widget"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Estimator',
              item: 'https://rooflyzer.pro/estimator',
            },
          ],
        }}
      />

      <section className="relative pt-28 pb-16 px-4 sm:px-6 text-center overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider mb-6">
              <Calculator className="w-4 h-4 text-emerald-400" weight="fill" />
              <span>Live Estimator</span>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <h1 className="hero-h1 font-bold text-white mb-5">
              Roof Estimates From <span className="text-white">Outer Space.</span>
            </h1>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <p className="hero-lead text-white/65 max-w-2xl mx-auto mb-10">
              Enter any U.S. address to get a satellite-based measurement and ballpark estimate in
              seconds. No signup required for the demo.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <form onSubmit={handleDemoSubmit} className="max-w-2xl mx-auto mb-4">
              <div className="flex flex-col sm:flex-row gap-3 p-2 rounded-xl bg-slate-900/60 border border-slate-800 focus-within:border-slate-600 focus-within:ring-1 focus-within:ring-slate-600/50">
                <div className="flex-1 flex items-center px-4">
                  <House className="w-5 h-5 text-slate-400 mr-3 flex-shrink-0" />
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="123 Main Street, Dallas, TX"
                    className="w-full bg-transparent text-white placeholder:text-slate-400 text-sm focus:outline-none py-3"
                    aria-label="Property address"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
                >
                  Get estimate
                  <ArrowRight className="ml-2 size-5" />
                </Button>
              </div>
            </form>
            <p className="text-xs text-white/40 mb-10">
              Demo uses public satellite data. For contractor-grade accuracy, sign in to the full
              platform.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.4}>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                className="rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
                onClick={() => window.location.assign(getAppLink('/estimate'))}
              >
                Try the full estimator
                <ArrowSquareOut className="ml-2 size-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-lg border-slate-700 bg-slate-800/60 text-slate-200 hover:text-white hover:bg-slate-700/60 px-8"
                asChild
              >
                <Link to="/demo">Book a demo</Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl overflow-hidden border border-white/[0.08] bg-[#050507]">
            <img
              src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?q=80&w=2400&auto=format&fit=crop"
              alt="Aerial view of suburban rooftops for satellite measurement"
              className="w-full h-64 sm:h-96 object-cover opacity-80"
              loading="eager"
            />
            <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/[0.08]">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-sm text-white/70">Live demo available now</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-700/60 hover:text-white"
                onClick={() => window.location.assign(getAppLink('/estimate'))}
              >
                Open estimator
                <ArrowSquareOut className="ml-2 size-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="section-h2 font-bold text-white mb-4">Two products. One address.</h2>
            <p className="body-lg text-white/55">
              Rooflyzer serves both homeowners and contractors with different accuracy needs.
            </p>
          </AnimatedSection>

          <div className="grid lg:grid-cols-2 gap-6">
            <AnimatedSection delay={0.1}>
              <div className="h-full rounded-3xl bg-slate-900/60 border border-slate-800 p-8 sm:p-10 flex flex-col">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-xs font-mono uppercase tracking-wider text-emerald-400 w-fit mb-6">
                  <Lightning className="w-4 h-4" weight="fill" />
                  <span>Live now</span>
                </div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-slate-800 border border-slate-700/80 flex items-center justify-center">
                    <Globe className="w-7 h-7 text-emerald-400" weight="bold" />
                  </div>
                  <h3 className="section-h3 font-bold text-white">Instant Estimate Widget</h3>
                </div>
                <p className="body-lg text-white/60 mb-6 flex-grow">
                  A homeowner-facing embed for your website. Captures leads and generates ballpark
                  roof estimates using free ArcGIS building footprints or paid Google Solar API. The
                  widget is live and ready to install today.
                </p>
                <ul className="space-y-3 mb-8">
                  {widgetBenefits.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-white/70">
                      <Check
                        className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5"
                        weight="bold"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold shadow-sm"
                  onClick={() => window.location.assign(getAppLink('/estimate'))}
                >
                  Try the widget
                  <ArrowRight className="ml-2 size-5" />
                </Button>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="h-full rounded-3xl bg-slate-900/40 border border-slate-800/80 p-8 sm:p-10 flex flex-col">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-mono uppercase tracking-wider text-slate-400 w-fit mb-6">
                  <Calendar className="w-4 h-4" />
                  <span>Planned</span>
                </div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-slate-800 border border-slate-700/80 flex items-center justify-center">
                    <Ruler className="w-7 h-7 text-slate-300" weight="bold" />
                  </div>
                  <h3 className="section-h3 font-bold text-white">Professional Measurement Tool</h3>
                </div>
                <p className="body-lg text-white/60 mb-6 flex-grow">
                  A contractor-facing measurement engine for insurance-ready reports with higher
                  accuracy. The AI models and infrastructure are planned; the measurement engine is
                  not yet deployed. Join the waitlist to get early access.
                </p>
                <ul className="space-y-3 mb-8">
                  {measurementBenefits.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-white/70">
                      <Check
                        className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5"
                        weight="bold"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant="outline"
                  className="w-full rounded-lg border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-700/60 hover:text-white"
                  asChild
                >
                  <Link to="/community/founders-beta-program">
                    Join the waitlist
                    <ArrowRight className="ml-2 size-5" />
                  </Link>
                </Button>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-3xl bg-slate-900/60 border border-slate-800 p-8 md:p-12 text-center space-y-6"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider">
              <CloudSun className="w-4 h-4 text-emerald-400" />
              <span>Built for storm season</span>
            </div>
            <h2 className="section-h2 font-bold text-white">
              Turn weather traffic into booked jobs
            </h2>
            <p className="body-lg text-white/60 max-w-xl mx-auto">
              Pair the Instant Estimate Widget with storm intelligence to capture homeowners while
              they are actively searching for a roofer.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                className="rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
                onClick={() => window.location.assign(getAppLink('/estimate'))}
              >
                Start estimating
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-lg border-slate-700 bg-slate-800/60 text-slate-200 hover:text-white hover:bg-slate-700/60 px-8"
                asChild
              >
                <Link to="/contact">
                  <Envelope className="mr-2 size-5" />
                  Talk to sales
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
